import { HighlightedPassengers } from 'components/flighteditor/unified/passenger-guidance';
import React from 'react';
import {Button, Checkbox, Dropdown, Empty, Icon, Menu, Tooltip, Typography} from 'antd';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faRoute} from '@fortawesome/free-solid-svg-icons';
import {DraggableBodyRow} from '../../TableDragSorting';
import ETable from 'components/enchanced-antd-table';
import {FlightRouteCard} from 'components/flighteditor/unified/flight-route-display';
import {createFlightLegLoadColumns, isPositioningLeg} from './flight-leg-load-columns';
import RemoveRedundantLegButton from './RemoveRedundantLegButton';
import {canRemoveEmptyReturnLeg} from 'common/flight-leg/util';
import './FlightScheduleLegs.less';

class FlightScheduleLegs extends React.Component {
    static contextType = HighlightedPassengers;
    render(){
        const {
            title = 'Flight Legs',
            extra = [],
            data = [],
            selectedLeg = 0,
            selectedLegs = [],
            selectionMode = 'single',
            selectedEntities = [],
            onLegChange
        } = this.props;
        const finalLegIndex = data.length - 1;
        const hasProtectedReturn = data.length > 0 &&
            data[finalLegIndex].destinationID === data[0].departureID;
        const isMultipleSelection = selectionMode === 'multiple';
        const selectedEntityIDs = new Set(selectedEntities.map(entity => entity._id));

        const isSelected = (index) => isMultipleSelection
            ? selectedLegs.includes(index)
            : index === selectedLeg;

        const loadColumns = createFlightLegLoadColumns(record => {
            const index = data.indexOf(record);
            const isProtectedReturn = hasProtectedReturn && index === finalLegIndex;
            const destination = record.destination || record.destinationID;
            const paxObjList = this.props.flight?.paxIDList?.filter(pax => (
                record.paxIDs?.includes(pax._id)
            ));
            const paxWithDeletedPerson = paxObjList?.filter(pax => !pax.personID);
            const isRedundant = this.props.checkLegIsRedundant?.(record, index);

            const status = (
                <>
                    {isProtectedReturn ? (
                        <Tooltip title="Final return leg cannot be reordered">
                            <Icon type="lock" className="flight-schedule-legs__lock"/>
                        </Tooltip>
                    ) : null}
                    {isRedundant ? (
                        <Tooltip title={`${destination} can be skipped here without dropping any PAX/CGO movement. Removal is recommended.`}>
                            <Typography.Text type="warning"><Icon type="warning"/></Typography.Text>
                        </Tooltip>
                    ) : null}
                    {paxWithDeletedPerson?.length ? (
                        <Tooltip title={`${paxWithDeletedPerson.length} ${paxWithDeletedPerson.length === 1 ? 'passenger has' : 'passengers have'} a deleted person record. Removal of these personnel is recommended.`}>
                            <Typography.Text type="danger"><Icon type="warning"/></Typography.Text>
                        </Tooltip>
                    ) : null}
                </>
            );

            return status;
        }, leg => ({
            pax: new Set((leg.paxIDs || []).filter(id => selectedEntityIDs.has(id))).size,
            cgo: new Set((leg.cgoIDs || []).filter(id => selectedEntityIDs.has(id))).size
        }));
        loadColumns.push({
            title: '',
            key: 'actions',
            className: 'flight-schedule-legs__actions',
            width: 40,
            align: 'center',
            render: (_, record, index) => {
                const emptyReturn = canRemoveEmptyReturnLeg(data, index);
                const finalReturn = hasProtectedReturn && index === finalLegIndex;
                let onRemove;
                if (emptyReturn) {
                    onRemove = this.props.onRemoveEmptyReturnLeg;
                } else if (!finalReturn && this.props.checkLegIsRedundant?.(record, index)) {
                    onRemove = this.props.onRemoveRedundantLeg;
                }
                return (
                    <RemoveRedundantLegButton
                        compact
                        disabled={this.props.disabled}
                        emptyReturn={emptyReturn}
                        originOnly={emptyReturn && data.length === 1}
                        onRemove={onRemove ? () => onRemove(record, index) : undefined}
                    />
                );
            }
        });
        const columns = isMultipleSelection ? [{
            title: '',
            key: 'checkbox',
            width: 28,
            render: (_, record, index) => (
                <Checkbox
                    checked={isSelected(index)}
                    aria-label={`Select leg ${index + 1}`}
                    onClick={event => {
                        event.stopPropagation();
                        onLegChange?.(index);
                    }}
                />
            )
        }, ...loadColumns] : loadColumns;

        const canReorder = !this.props.disabled && !!this.props.onReorder;

        return (
            <FlightRouteCard
                className="flight-schedule-legs"
                title={title}
                legCount={data.length}
                compactHeader
                showTitleIcon={false}
                headerExtra={(
                    <div className="flight-schedule-legs__header-actions">
                        {extra.map((item, index) => <React.Fragment key={index}>{item}</React.Fragment>)}
                        <Dropdown
                            disabled={this.props.makingRoundTrip}
                            trigger={['click']}
                            placement="bottomRight"
                            overlay={(
                                <Menu>
                                    <Menu.Item
                                        key="make-round-trip"
                                        disabled={hasProtectedReturn || this.props.disabled || this.props.makingRoundTrip ||
                                            !this.props.onMakeRoundTrip || !data[0]?.departureID ||
                                            !data[finalLegIndex]?.destinationID}
                                        onClick={() => this.props.onMakeRoundTrip?.()}
                                    >
                                        Make round trip
                                    </Menu.Item>
                                </Menu>
                            )}
                        >
                            <Button
                                className="flight-schedule-legs__header-action flight-schedule-legs__more"
                                icon="more"
                                loading={this.props.makingRoundTrip}
                                disabled={this.props.makingRoundTrip}
                                aria-busy={!!this.props.makingRoundTrip}
                                aria-label={this.props.makingRoundTrip ? 'Making flight round trip' : 'More flight leg actions'}
                                aria-haspopup="menu"
                            />
                        </Dropdown>
                    </div>
                )}
            >
                {!data.length ? (
                    <Empty
                        className="flight-schedule-legs__empty"
                        image={<FontAwesomeIcon icon={faRoute} className="flight-schedule-legs__empty-icon" aria-hidden="true"/>}
                        imageStyle={{height: 64}}
                        description={false}
                    >
                        <div className="flight-schedule-legs__empty-instructions">
                            <Typography.Paragraph>
                                Click <Typography.Text strong>Auto Assign</Typography.Text> to assign passengers and automatically build a flight route.
                            </Typography.Paragraph>
                            <Typography.Paragraph>
                                Click <Typography.Text strong>Create Route</Typography.Text> to manually create a route.
                            </Typography.Paragraph>
                        </div>
                    </Empty>
                ) : (
                    <ETable
                        className="mc-table draggable-table flight-schedule-legs__table mc-flight-leg-loads"
                        dataSource={data}
                        columns={columns}
                        rowKey={(_, index) => index}
                        size="small"
                        pagination={false}
                        components={canReorder ? {
                            body: {
                                row: DraggableBodyRow
                            }
                        } : undefined}
                        onRow={(row, index) => {
                            const highlighted = (row.paxIDs || []).some(id => this.context.has(id));
                            const isProtectedReturn = hasProtectedReturn && index === finalLegIndex;
                            return {
                                moveRow: (dragIndex, hoverIndex) => canReorder &&
                                    this.props.onReorder(dragIndex, hoverIndex),
                                onClick: () => onLegChange?.(index),
                                index,
                                canDrag: canReorder && !isProtectedReturn,
                                canDrop: canReorder && !isProtectedReturn,
                                title: highlighted ? 'Leg containing the target passenger' : undefined,
                                style: {cursor: 'pointer'},
                                className: [
                                    'flight-schedule-legs__row',
                                    isPositioningLeg(row) && index > 0 && isPositioningLeg(data[index - 1])
                                        ? 'flight-schedule-legs__row--positioning-inbound' : '',
                                    isPositioningLeg(row) && index < finalLegIndex && isPositioningLeg(data[index + 1])
                                        ? 'flight-schedule-legs__row--positioning-outbound' : '',
                                    isSelected(index) ? 'selected' : '',
                                    highlighted ? 'mc-pulse-highlight' : ''
                                ].filter(Boolean).join(' ')
                            };
                        }}
                    />
                )}
            </FlightRouteCard>
        );
    }
}

export default FlightScheduleLegs
